import { Link } from 'react-router-dom'
import { MapPin, BookOpen, Heart, ArrowRight, BadgeCheck, Star, Check, Clock, Sparkles } from 'lucide-react'
import { motion } from 'framer-motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { SPECIALTY_LABELS } from '@/lib/constants'
import type { InstructorWithProfile } from '@/types/database'
import { useAuth } from '@/hooks/useAuth'
import { useFavoriteIds, useToggleFavorite } from '@/hooks/useFavorites'
import { cn } from '@/lib/utils'
import { ProBadge } from '@/components/instructor/ProBadge'

interface InstructorCardProps {
  instructor: InstructorWithProfile
  isComparing?: boolean
  onCompareToggle?: () => void
}

function getInitials(name: string | null): string {
  if (!name) return '?'
  return name.split(' ').filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join('')
}

function formatCurrency(value: number | null): string {
  if (value === null) return 'Consultar'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency', currency: 'BRL',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value)
}

function getResponseTime(instructor: InstructorWithProfile): string | null {
  const lessons = instructor.total_lessons ?? 0
  const rating = instructor.rating_avg ?? 0
  if (lessons === 0) return null
  if (rating >= 4.7 && lessons >= 20) return '< 1h'
  if (lessons >= 10) return '~2h'
  return '~4h'
}

export function InstructorCard({ instructor, isComparing, onCompareToggle }: InstructorCardProps) {
  const { profile: authProfile } = useAuth()
  const { data: favIds } = useFavoriteIds()
  const toggleFav = useToggleFavorite()
  const isStudent = authProfile?.role === 'student'
  const isFav = favIds?.includes(instructor.id) ?? false

  const { profile, rating_avg, total_lessons, hourly_rate, bio, slug, status } = instructor
  const isWoman = (instructor as InstructorWithProfile & { is_woman_instructor?: boolean }).is_woman_instructor === true
  const isPro = profile.is_pro_instructor === true
  const isFeatured = (instructor as InstructorWithProfile & { is_featured?: boolean }).is_featured === true
  const responseTime = getResponseTime(instructor)
  const rawSpecialties = ((instructor as any).specialties ?? []) as (string | { specialty: string })[]
  const specialties: string[] = rawSpecialties.map(s => typeof s === 'string' ? s : s.specialty)
  const visibleSpecialties = specialties.slice(0, 2)
  const extraCount = specialties.length - visibleSpecialties.length
  const location = [profile.city, profile.state].filter(Boolean).join(', ')
  const profilePath = slug ? `/instrutores/${slug}` : '#'

  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ type: 'spring', stiffness: 360, damping: 28 }}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-card hover:shadow-card-hover transition-all duration-300',
        isComparing && 'ring-2 ring-brand-500 shadow-brand/20',
      )}
    >
      {/* Compare checkbox */}
      {onCompareToggle && (
        <button
          onClick={(e) => { e.preventDefault(); onCompareToggle() }}
          className={cn(
            'absolute top-3 left-3 z-20 flex h-6 w-6 items-center justify-center rounded-md border-2 transition-all duration-150',
            isComparing
              ? 'border-brand-500 bg-brand-500 text-white'
              : 'border-white/60 bg-white/20 text-transparent hover:border-white hover:bg-white/40',
          )}
          title={isComparing ? 'Remover da comparação' : 'Adicionar à comparação'}
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Header gradient band */}
      <div className="relative bg-gradient-to-br from-brand-700 via-brand-600 to-teal-600 px-5 pt-5 pb-11 overflow-hidden">
        <div className="absolute -top-8 -right-8 h-28 w-28 rounded-full bg-white/5" />
        <div className="absolute -bottom-4 -left-4 h-20 w-20 rounded-full bg-teal-500/20" />

        {/* Fav button */}
        {isStudent && (
          <button
            onClick={(e) => { e.preventDefault(); toggleFav.mutate({ instructorId: instructor.id, isFav }) }}
            className={cn(
              'absolute top-3.5 right-3.5 z-10 p-1.5 rounded-full transition-all duration-200',
              isFav
                ? 'bg-rose-500 text-white shadow-sm scale-110'
                : 'bg-white/15 text-white/60 hover:bg-white/25 hover:text-white hover:scale-110',
            )}
          >
            <Heart className={`h-3.5 w-3.5 ${isFav ? 'fill-current' : ''}`} />
          </button>
        )}

        {/* Rating chip */}
        <div className="absolute bottom-3.5 right-4 flex items-center gap-1 bg-black/20 backdrop-blur-sm rounded-full px-2.5 py-1">
          <Star className="h-3 w-3 fill-orange-300 text-orange-300" />
          <span className="text-xs font-bold text-white">
            {rating_avg != null ? rating_avg.toFixed(1) : '—'}
          </span>
        </div>
      </div>

      {/* Avatar row */}
      <div className="relative -mt-8 px-5">
        <div className="flex items-end justify-between">
          <div className="relative">
            <Avatar className="h-16 w-16 ring-4 ring-white shadow-md">
              <AvatarImage src={profile.avatar_url ?? undefined} alt={profile.full_name ?? 'Instrutor'} />
              <AvatarFallback className="bg-brand-100 text-brand-700 text-lg font-bold">
                {getInitials(profile.full_name)}
              </AvatarFallback>
            </Avatar>
            {status === 'approved' && (
              <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm">
                <BadgeCheck className="h-4 w-4 text-teal-500" />
              </span>
            )}
          </div>
          <div className="pb-1 text-right">
            <p className="text-[10px] text-muted-foreground leading-none mb-0.5">por hora</p>
            <p className="text-xl font-extrabold text-brand-700 leading-none tracking-tight">
              {formatCurrency(hourly_rate)}
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 px-5 pt-3 pb-5 gap-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900 leading-snug flex items-center gap-1.5 flex-wrap">
            {profile.full_name ?? 'Instrutor'}
            {isPro && <ProBadge />}
          </h3>
          {location && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0 text-teal-500" />
              {location}
            </p>
          )}
        </div>

        {bio && (
          <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{bio}</p>
        )}

        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-gray-50 rounded-full px-2 py-0.5">
            <BookOpen className="h-3 w-3 text-brand-400" />
            {total_lessons.toLocaleString('pt-BR')} aulas
          </span>
          {responseTime && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
              <Clock className="h-3 w-3" />
              Responde em {responseTime}
            </span>
          )}
          {isFeatured && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-medium text-yellow-700 bg-yellow-50 rounded-full px-2 py-0.5"
              title="Instrutor em destaque"
            >
              <Sparkles className="h-3 w-3" />
              Destaque
            </span>
          )}
          {isWoman && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-medium text-pink-700 bg-pink-50 rounded-full px-2 py-0.5"
              title="Instrutora autodeclarada"
            >
              Instrutora
            </span>
          )}
          {visibleSpecialties.map((sp) => (
            <Badge key={sp} variant="teal" className="text-[10px] px-1.5 py-0.5 font-medium">
              {SPECIALTY_LABELS[sp as keyof typeof SPECIALTY_LABELS] ?? sp}
            </Badge>
          ))}
          {extraCount > 0 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 text-gray-400 border-gray-200">
              +{extraCount}
            </Badge>
          )}
        </div>

        <div className="mt-auto pt-3 border-t border-gray-100">
          <Button
            asChild
            size="sm"
            className="w-full bg-brand-600 hover:bg-brand-700 text-white h-9 text-xs font-semibold gap-1.5 group/btn"
          >
            <Link to={profilePath}>
              Ver perfil completo
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover/btn:translate-x-0.5" />
            </Link>
          </Button>
        </div>
      </div>
    </motion.div>
  )
}
